import {
  DisplayResultMessage,
  NotificationActionTypeData,
  OpenMessage,
  LanguageNameSpaces,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import {
  PLATFORM_TO_DISPLAY_NAME,
  PLATFORM_TO_KEY,
} from "@shared/features/settings/constants";
import {
  NotificationActionType,
  Platform,
} from "@shared/features/settings/types";
import { useEffect, useState } from "react";
import { isArray } from "lodash";
import {
  getNotificationSettingsSections,
  getSectionItem,
} from "@shared/features/settings/utils";
import { translateOptionName } from "@shared/common/i18n/optionNames";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export const useNotificationsSettings = (
  displayResultMessage: DisplayResultMessage,
  openMessage: OpenMessage,
  openPlatformDisabledMessage: (platform: Platform) => void
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const { data: notificationsData, isLoading: getNotificationsDataLoading } =
    sharedLib.sharedApi.notifications.useGetNotificationsDataQuery();
  const [
    bulkChangeNotificationAction,
    { isLoading: changeNotificationActionLoading },
  ] =
    sharedLib.sharedApi.notifications.useBulkChangeNotificationActionMutation();
  const [changePlatformIsActive, { isLoading: changePlatformIsActiveLoading }] =
    sharedLib.sharedApi.notifications.useChangePlatformIsActiveMutation();

  const loading = getNotificationsDataLoading || changePlatformIsActiveLoading;
  const sections = getNotificationSettingsSections(notificationsData) || [];
  const [expanded, setExpanded] = useState<Record<string, boolean>>();

  useEffect(() => {
    if (sections.length && !expanded) {
      const initialState = sections.reduce(
        (initialValue, section) => ({ ...initialValue, [section]: true }),
        {}
      );
      setExpanded(initialState);
    }
  }, [sections]);

  const onSwitchClick = (
    platform: Platform,
    isActive: boolean,
    disabled?: boolean
  ) => {
    if (disabled) {
      openPlatformDisabledMessage?.(platform);
    } else {
      displayResultMessage(
        changePlatformIsActive({
          telegram_coupling_type: platform,
          is_active: isActive,
        }).unwrap()
      ).then(() =>
        openMessage(
          t(
            isActive ? "notifications.enabled_on" : "notifications.disabled_on",
            {
              platform: translateOptionName(PLATFORM_TO_DISPLAY_NAME[platform]),
            }
          ),
          "success"
        )
      );
    }
  };

  const onExpandCollapseClick = (section: string) => {
    setExpanded((prevState) => ({
      ...prevState,
      [section]: !expanded?.[section],
    }));
  };

  const onCheckboxClick = (
    actionTypes: NotificationActionType | NotificationActionType[] | undefined,
    platform: Platform,
    isActive: boolean
  ) => {
    if (!actionTypes) return;
    const platformActive = notificationsData?.[PLATFORM_TO_KEY[platform]];
    if (platformActive) {
      const notificationActionTypes = isArray(actionTypes)
        ? actionTypes
        : [actionTypes];
      displayResultMessage(
        bulkChangeNotificationAction({
          notification_action_types: notificationActionTypes,
          telegram_coupling_type: platform,
          is_active: isActive,
        }).unwrap()
      ).then(() => openMessage(t("notifications.settings_updated"), "success"));
    } else {
      const disabled = platformActive === null || platformActive === undefined;
      if (disabled) openPlatformDisabledMessage?.(platform);
      else {
        openMessage?.(
          t("notifications.enable_toggle_first", {
            platform: translateOptionName(PLATFORM_TO_DISPLAY_NAME[platform]),
          }),
          "warning"
        );
      }
    }
  };

  const getSectionActionTypes = (section: string) => {
    return getSectionItem(notificationsData, section)?.data;
  };

  const getSectionToActionTypes = () => {
    return sections?.reduce(
      (mapping: Record<string, NotificationActionTypeData[]>, section) => {
        const actionTypes = getSectionActionTypes(section);
        if (actionTypes) {
          mapping[section] = actionTypes;
        }
        return mapping;
      },
      {}
    );
  };

  return {
    notificationsData,
    expanded,
    onSwitchClick,
    onExpandCollapseClick,
    onCheckboxClick,
    loading,
    getNotificationsDataLoading,
    changeNotificationActionLoading,
    getSectionToActionTypes,
  };
};
